/**
 * 修改自 https://github.com/think2011/localResizeIMG/blob/master/src/lrz.js
 */
import JPEGEncoder from './JPEGEncoder';
import dataURItoBlob from './dataURItoBlob';
import exif from './exif';
import { type } from './util';

const UA = (function (userAgent) {
  const isOldAndroid = /Android (\d.*?)/g.exec(userAgent) || /Android\/(\d.*?) /g.exec(userAgent);
  // 判断设备是否是IOS7以下
  // 判断设备是否是android4.5以下
  // 判断是否iOS
  // 判断是否android
  // 判断是否QQ浏览器
  return {
    oldAndroid: isOldAndroid ? +isOldAndroid.pop().substr(0, 3) < 4.5 : false,
    iOS: /\(i[^]+( U)? CPU.+Mac OS X/.test(userAgent),
    android: /Android/g.test(userAgent),
    mQQBrowser: /MQQBrowser/g.test(userAgent),
  };
}(navigator.userAgent));

const __defaults = {
  width: null,
  height: null,
  quality: 0.7,
};

function createBase64({
  resize, img, canvas, ctx, defaults, orientation
}) {
  // 调整为正确方向
  switch (orientation) {
    case 3:
      ctx.rotate(180 * Math.PI / 180);
      ctx.drawImage(img, -resize.width, -resize.height, resize.width, resize.height);
      break;
    case 6:
      ctx.rotate(90 * Math.PI / 180);
      ctx.drawImage(img, 0, -resize.width, resize.height, resize.width);
      break;
    case 8:
      ctx.rotate(270 * Math.PI / 180);
      ctx.drawImage(img, -resize.height, 0, resize.height, resize.width);
      break;

    case 2:
      ctx.translate(resize.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, 0, 0, resize.width, resize.height);
      break;
    case 4:
      ctx.translate(resize.width, 0);
      ctx.scale(-1, 1);
      ctx.rotate(180 * Math.PI / 180);
      ctx.drawImage(img, -resize.width, -resize.height, resize.width, resize.height);
      break;
    case 5:
      ctx.translate(resize.width, 0);
      ctx.scale(-1, 1);
      ctx.rotate(90 * Math.PI / 180);
      ctx.drawImage(img, 0, -resize.width, resize.height, resize.width);
      break;
    case 7:
      ctx.translate(resize.width, 0);
      ctx.scale(-1, 1);
      ctx.rotate(270 * Math.PI / 180);
      ctx.drawImage(img, -resize.height, 0, resize.height, resize.width);
      break;

    default:
      ctx.drawImage(img, 0, 0, resize.width, resize.height);
  }

  return new Promise((resolve) => {
    if (UA.oldAndroid || UA.mQQBrowser || !navigator.userAgent) {
      const encoder = new JPEGEncoder();
      img = ctx.getImageData(0, 0, canvas.width, canvas.height);

      resolve(encoder.encode(img, defaults.quality * 100));
    } else {
      resolve(canvas.toDataURL('image/jpeg', defaults.quality));
    }
  });
}

function alignHeight(ret, height, scale) {
  ret.height = height;
  ret.width = Math.ceil(height * scale);
}

function alignWidth(ret, width, scale) {
  ret.width = width;
  ret.height = Math.ceil(width / scale);
}

function getResize({ img, defaults, orientation }) {
  let { width, height } = defaults;

  let ret = {
    width: img.width,
    height: img.height,
  };

  if ('5678'.indexOf(orientation) > -1) {
    ret.width = img.height;
    ret.height = img.width;
  }

  // 如果原图小于设定，采用原图
  if (ret.width < width || ret.height < height) {
    return ret;
  }

  let scale = ret.width / ret.height;

  if (width && height) {
    if (scale >= width / height) {
      if (ret.width > width) {
        alignWidth(ret, width, scale);
      }
    } else {
      if (ret.height > height) {
        alignHeight(ret, height, scale);
      }
    }
  } else if (width) {
    if (width < ret.width) {
      alignWidth(ret, width, scale);
    }
  } else if (height) {
    if (height < ret.height) {
      alignHeight(ret, height, scale);
    }
  }

  // 超过这个值base64无法生成，在IOS上
  while (ret.width >= 3264 || ret.height >= 2448) {
    ret.width *= 0.8;
    ret.height *= 0.8;
  }

  return ret;
}

function getBase64({
  img, defaults, file, canvas
}) {
  return new Promise((resolve, reject) => {
    try {
      // 传入blob在android4.3以下有bug
      exif.getData(typeof file === 'object' ? file : img, function () {
        const orientation = exif.getTag(this, 'Orientation');

        const resize = getResize({ img, defaults, orientation });
        const ctx = canvas.getContext('2d');

        canvas.width = resize.width;
        canvas.height = resize.height;

        // 设置为白色背景，jpg是不支持透明的，所以会被默认为canvas默认的黑色背景。
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        createBase64({
          resize, img, canvas, ctx, defaults, orientation
        }).then(resolve);
      });
    } catch (err) {
      reject(err);
    }
  });
}

function init({ file, defaults }) {
  const fileIsString = type.isString(file);
  const fileIsBase64 = fileIsString && /^data:/.test(file);
  const img = new Image();
  const canvas = document.createElement('canvas');
  const blob = fileIsString ? file : URL.createObjectURL(file);

  if (!canvas.getContext) {
    throw new Error('浏览器不支持canvas');
  }

  return new Promise((resolve, reject) => {
    img.onerror = () => reject('加载图片文件失败');

    img.onload = function () {
      getBase64({
        img, defaults, file, canvas
      })
        .then((base64) => {
          if (base64.length < 10) {
            reject('生成base64失败');
          } else {
            return base64;
          }
        })
        .then((base64) => {
          // 压缩文件太大就采用源文件,且使用原生的FormData()
          if (!(typeof file === 'object' && base64.length > file.size)) {
            file = dataURItoBlob(base64);
          }

          resolve({
            base64,
            base64Len: base64.length,
            file,
            fileLen: +file.size,
            origin: file,
          });

          if (!fileIsString) URL.revokeObjectURL(blob);
        });
    };

    // 如果传入的是base64在移动端会报错
    if (!fileIsBase64) {
      img.crossOrigin = 'anonymous';
    }

    img.src = blob;
  });
}

export default function Lrz(file, opts = {}) {
  if (!file) throw new Error('没有收到图片，可能的解决方案：https://github.com/think2011/localResizeIMG/issues/7');

  const defaults = { ...__defaults, ...opts };

  console.log('123 chooseImageFn Lrz');

  return init({ file, defaults });
}
